import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import Share from 'components/Dashboard/components/Share';
import ThumbsDown from 'components/Common/ThumbsDown';
import useCreateMarkup from 'components/Dashboard/hooks/useCreateMarkup';
import RegulationsDocketOverviewTab from 'components/Dashboard/Regulations/RegulationsDocketOverviewTab';
import RegulationsDocumentsTab from 'components/Dashboard/Regulations/RegulationsDocumentsTab';
import ContentSummary from 'components/Dashboard/components/ContentSummary';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import { store } from 'components/Store';
import time from 'utils/time';

const TAB_HASH = {
  overview: 'overview',
  documents: 'documents',
};

const tabFromHash = () => {
  const key = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
  return TAB_HASH[key] ? key : 'overview';
};

const RegulationsDocketPage = ({ currentNew, hideSummary }) => {
  const { createMarkup } = useCreateMarkup();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(tabFromHash);
  const { state, dispatch } = useContext(store);
  const { regulationDocketScrollPosition } = state;
  const [heightContainer, containerRef] = useHeightContainer();
  const overviewScrollbarRef = useRef(null);
  const documentsScrollbarRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  const documentsCount = currentNew?.documents?.length ?? 0;

  useEffect(() => {
    setActiveTab(tabFromHash());
  }, [location.hash]);

  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (activeTab !== 'documents') return;
    if (regulationDocketScrollPosition === null || regulationDocketScrollPosition < 0) return;
    if (!documentsScrollbarRef.current) return;
    if (documentsCount === 0) return;
    if (heightContainer <= 0) return;

    const targetScrollTop = regulationDocketScrollPosition;
    scrollRestoredRef.current = true;

    const apply = () => {
      const scroller = documentsScrollbarRef.current;
      if (!scroller) return;
      scroller.scrollTop(targetScrollTop);
      const view = scroller.view;
      if (view && view.scrollTop !== targetScrollTop) {
        view.scrollTop = targetScrollTop;
      }
      dispatch({ type: 'MODIFY_SECTION', parameter: 'regulationDocketScrollPosition', value: null });
    };

    requestAnimationFrame(() => requestAnimationFrame(apply));
  }, [activeTab, regulationDocketScrollPosition, documentsCount, heightContainer, dispatch]);

  const selectTab = (tab) => {
    navigate({ hash: TAB_HASH[tab] }, { replace: true });
  };

  const { id, title, objectId, objectType, canonicalUrl } = currentNew ?? {};
  const documents = currentNew?.documents ?? [];
  const item = {
    id,
    title,
    objectID: objectId,
    type: objectType,
  };

  const documentsTitle = `Documents${documents.length > 0 ? ` (${documents.length})` : ''}`;
  const scrollerHeight = Math.max(heightContainer - 95, 0);

  return (
    <div className='regulations-content-page'>
      <div className='section-title pb-2 regulations-content-title p-3 p-lg-4'>
        <h3 className='main-title news-title pr-lg-4' dangerouslySetInnerHTML={createMarkup(currentNew?.title)} />
        <div className='row mx-0 justify-content-between align-items-center'>
          <p className='heading-date mb-0'>
            {!!currentNew?.agency && (
              <>
                {currentNew.agency}
                {(currentNew?.documentType || currentNew?.postedDate || currentNew?.sourceUrl) && (
                  <span className='dot'></span>
                )}
              </>
            )}
            {!!currentNew?.documentType && (
              <>
                {currentNew.documentType}
                {(currentNew?.postedDate || currentNew?.sourceUrl) && (
                  <span className='dot'></span>
                )}
              </>
            )}
            {!!currentNew?.postedDate && (
              <>
                <span className='date'>{time(currentNew.postedDate).formatGeneralDate()}</span>
                {!!currentNew?.sourceUrl && <span className='dot'></span>}
              </>
            )}
            {!!currentNew?.sourceUrl && (
              <a
                className='legislation-source mb-0'
                href={currentNew.sourceUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                Go to source
              </a>
            )}
          </p>
          <div className='flex-centered'>
            <ThumbsDown
              item={{ objectID: currentNew?.objectId }}
              nameSection='Regulations'
              show={!!currentNew?.keywordsFromLists}
              contentPage={true}
              alreadyMarked={!!currentNew?.feedback}
              keywordsFromLists={currentNew?.keywordsFromLists}
              thick={true}
            />
            <Share
              item={item}
              id={id}
              title={title}
              link={canonicalUrl ? `${import.meta.env.VITE_APP_APPURL}${canonicalUrl}` : undefined}
            />
          </div>
        </div>
        <div className='pt-3' ref={containerRef}>
          <button
            className={`general-button ${activeTab === 'overview' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1 mr-3`}
            onClick={() => selectTab('overview')}
          >
            Overview
          </button>
          <button
            className={`general-button ${activeTab === 'documents' ? 'action-button' : 'hovered-button'} border rounded-pill px-3 py-1`}
            onClick={() => selectTab('documents')}
          >
            {documentsTitle}
          </button>
        </div>
      </div>
      {activeTab === 'overview' && (
        <CustomScrollbar
          className='simple-scrollbar indented-scrollbar-small'
          style={{ height: `${scrollerHeight}px` }}
          minimalThumbYSize={55}
          maximalThumbYSize={100}
          ref={overviewScrollbarRef}
        >
          <ContentSummary content={currentNew} hide={hideSummary} />
          <div className='px-3 px-lg-4 pt-3 pb-3 pb-lg-4'>
            <RegulationsDocketOverviewTab currentNew={currentNew} />
          </div>
        </CustomScrollbar>
      )}
      {activeTab === 'documents' && (
        <CustomScrollbar
          className='simple-scrollbar indented-scrollbar-small'
          style={{ height: `${scrollerHeight}px` }}
          minimalThumbYSize={55}
          maximalThumbYSize={100}
          ref={documentsScrollbarRef}
        >
          <div className='px-3 px-lg-4 pt-3 pb-3 pb-lg-4'>
            <RegulationsDocumentsTab documents={documents} scrollbarRef={documentsScrollbarRef} />
          </div>
        </CustomScrollbar>
      )}
    </div>
  );
};

export default RegulationsDocketPage;
